import { Component, computed, inject, input, signal, OnInit, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { JSONSchema7Type } from 'json-schema';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { SfoUiJSONSchema7 } from '../../../metadata.model';

interface DatePartsValidationState {
  invalid: boolean;
  errors: ValidationErrors | null;
  dirty: boolean;
  touched: boolean;
}

@Component({
  selector: 'sfo-date-variable-input',
  templateUrl: './date-variable-input.component.html',
  styleUrls: ['./date-variable-input.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatHint,
    MatButton,
    MatIconButton,
    MatIcon,
    MatTooltip,
  ],
})
export class DateVariableInputComponent implements OnInit, OnDestroy {
  schema = input<SfoUiJSONSchema7 | null | undefined>();
  aFormControl = input.required<FormGroup>();
  defaultValue = input<JSONSchema7Type | unknown | undefined>();

  private formArrayValidationState = signal<DatePartsValidationState | null>(null);
  /** Tracks focus on the start-date input (index 0) only - that's the only
   * field the format hint is rendered under. */
  private isStartDateFocused = signal<boolean>(false);

  private readonly fb = inject(FormBuilder);
  private readonly inputSubject = new Subject<{ index: number; value: string }>();
  private destroy$ = new Subject<void>();

  shouldShowErrors = computed(() => {
    const validationState = this.formArrayValidationState();
    const hasCslValidationErrors = validationState?.errors?.['cslDateConsistency'];
    const shouldRespectFormState =
      validationState?.invalid &&
      hasCslValidationErrors &&
      (validationState?.dirty || validationState?.touched);

    return hasCslValidationErrors || shouldRespectFormState;
  });

  getErrorMessage = computed(() => {
    const validationState = this.formArrayValidationState();
    const cslError = validationState?.errors?.['cslDateConsistency'];

    return cslError?.message || 'Please check your input';
  });

  /**
   * The format hint (schema description) is only useful while the author is
   * actively typing, or once the text they left behind fails to parse
   * (surfaced via the existing error path, `shouldShowErrors`). A valid,
   * unfocused field hides it - it's not permanent chrome under the field.
   */
  showFormatHint = computed(() => this.isStartDateFocused() || this.shouldShowErrors());

  constructor() {}

  ngOnInit(): void {
    this.setupFormValidation();

    this.inputSubject
      .pipe(takeUntil(this.destroy$), debounceTime(500))
      .subscribe(({ index, value }) => {
        this.processDateInput(index, value);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get dateParts(): FormArray {
    return this.aFormControl().get('date-parts') as FormArray;
  }

  addDateParts(): void {
    if (this.dateParts.length < 2) {
      this.dateParts.push(
        this.fb.array([this.fb.control(''), this.fb.control(''), this.fb.control('')]),
      );
      this.markFormAsDirty();
      this.dateParts.updateValueAndValidity();
    }
  }

  removeDateParts(index: number): void {
    if (this.dateParts.length > 1) {
      this.dateParts.removeAt(index);
      this.markFormAsDirty();
      this.dateParts.updateValueAndValidity();
    }
  }

  getDatePartsAsString(index: number): string {
    const datePartsValues = this.getDateParts(index).controls.map((control) => control.value ?? '');
    let lastFilledIndex = -1;

    for (let i = datePartsValues.length - 1; i >= 0; i--) {
      if (datePartsValues[i] !== '') {
        lastFilledIndex = i;
        break;
      }
    }

    if (lastFilledIndex === -1) {
      return '';
    }

    return datePartsValues.slice(0, lastFilledIndex + 1).join('-');
  }

  updateDatePartsFromString(index: number, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.inputSubject.next({ index, value });
  }

  /** Only the start-date input (index 0) drives the hint; end-date focus is a no-op. */
  onDateInputFocus(index: number): void {
    if (index === 0) {
      this.isStartDateFocused.set(true);
    }
  }

  onDateInputBlur(index: number): void {
    if (index === 0) {
      this.isStartDateFocused.set(false);
    }
  }

  getDateParts(index: number): FormArray {
    return this.dateParts.at(index) as FormArray;
  }

  private setupFormValidation(): void {
    this.dateParts.setValidators([this.cslDateConsistencyValidator()]);

    const updateValidationState = () => {
      this.formArrayValidationState.set({
        invalid: this.dateParts.invalid,
        errors: this.dateParts.errors,
        dirty: this.dateParts.dirty,
        touched: this.dateParts.touched,
      });
    };

    this.dateParts.valueChanges.subscribe(updateValidationState);
    this.dateParts.statusChanges.subscribe(updateValidationState);

    updateValidationState();
  }

  private processDateInput(index: number, value: string): void {
    const parsedValues = this.parseDateString(value);
    const datePartsArray = this.getDateParts(index);

    datePartsArray.patchValue(parsedValues, { emitEvent: false });
    this.markFormAsDirty();

    this.dateParts.updateValueAndValidity({ emitEvent: true });
    this.aFormControl().updateValueAndValidity({ emitEvent: true });

    this.updateValidationState();
  }

  private parseDateString(value: string): [string, string, string] {
    if (value === '') {
      return ['', '', ''];
    }

    if (!/[-/,]/.test(value) && value.trim() === '') {
      return [value, '', ''];
    }

    const containsExplicitSeparator = /[-/,]/.test(value);
    const dateParts = containsExplicitSeparator
      ? value.split(/[-/,]/).map((part) => part.trim())
      : value.trim().split(/\s+/);

    if (dateParts.length === 1 && dateParts[0] === '') {
      return ['', '', ''];
    }

    return [dateParts[0] ?? '', dateParts[1] ?? '', dateParts[2] ?? ''];
  }

  private markFormAsDirty(): void {
    const formControl = this.aFormControl();
    formControl.markAsDirty();
    formControl.markAsTouched();

    this.dateParts.markAsDirty();
    this.dateParts.markAsTouched();
  }

  private updateValidationState(): void {
    this.formArrayValidationState.set({
      invalid: this.dateParts.invalid,
      errors: this.dateParts.errors,
      dirty: this.dateParts.dirty,
      touched: this.dateParts.touched,
    });
  }

  private cslDateConsistencyValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formArray = control as FormArray;

      if (!formArray || formArray.length <= 1) {
        return null;
      }

      const dateArrays = this.extractDateArrays(formArray);
      if (dateArrays.length <= 1) {
        return null;
      }

      const precisions = this.calculatePrecisions(dateArrays);
      const firstPrecision = precisions[0];

      if (this.hasPrecisionMismatch(precisions, firstPrecision)) {
        return this.createPrecisionMismatchError(precisions, firstPrecision);
      }

      return null;
    };
  }

  private extractDateArrays(formArray: FormArray): string[][] {
    const dateArrays: string[][] = [];

    for (let i = 0; i < formArray.length; i++) {
      const datePartsArray = formArray.at(i) as FormArray;
      if (!datePartsArray) continue;

      const nonEmptyValues = datePartsArray.controls
        .map((ctrl) => ctrl.value)
        .filter((value) => value !== null && value !== undefined && value !== '');

      if (nonEmptyValues.length > 0) {
        const fullValues = datePartsArray.controls.map((ctrl) => ctrl.value || '').slice(0, 3);

        dateArrays.push(fullValues.slice(0, Math.max(1, nonEmptyValues.length)));
      }
    }

    return dateArrays;
  }

  private calculatePrecisions(dateArrays: string[][]): number[] {
    return dateArrays.map(
      (arr) => arr.filter((val) => val !== null && val !== undefined && val !== '').length,
    );
  }

  private hasPrecisionMismatch(precisions: number[], firstPrecision: number): boolean {
    return !precisions.every((precision) => precision === firstPrecision);
  }

  private createPrecisionMismatchError(
    precisions: number[],
    firstPrecision: number,
  ): ValidationErrors {
    const precisionNames = ['year', 'year-month', 'year-month-day'];
    const firstPrecisionName = precisionNames[firstPrecision - 1] || `${firstPrecision} fields`;

    const mismatchedPrecisions = precisions
      .filter((p, i) => p !== firstPrecision && i > 0)
      .map((p) => precisionNames[p - 1] || `${p} fields`)
      .filter((name, i, arr) => arr.indexOf(name) === i);

    const errorMessage =
      `Date range format mismatch: first date has ${firstPrecisionName} format, ` +
      `but other date(s) have ${mismatchedPrecisions.join(', ')} format. ` +
      `All dates in a range must have the same number of components.`;

    return {
      'cslDateConsistency': {
        message: errorMessage,
        precisions: precisions,
        firstPrecision: firstPrecision,
      },
    };
  }
}
